import Fuse from 'fuse.js';

import { fDate } from 'src/utils/format-time';

import type { SearchProvider, ProviderSearchResult } from './providers/types';

export type JobApiItem = {
  Id: string | number;
  Title?: string;
  ContentText?: string;
  LocationCity?: string;
  LocationCountry?: string;
  LocationCountryCode?: string;
  JobMode?: string;
  JobType?: string;
  Skills?: string[];
  OrganizationName?: string;
  OrganizationSlug?: string;
  OrganizationId?: string | number;
  Status?: string;
  DateCreated?: string;
  DateLastModified?: string;
};

export type PreparedJobForLLM = {
  id: string;
  title: string;
  description?: string;
  location: {
    city?: string;
    country?: string;
    countryCode?: string;
  };
  jobMode?: string;
  jobType?: string;
  skills: string[];
  organization?: {
    name?: string;
    slug?: string;
    id?: string;
  };
  createdAt?: string;
  updatedAt?: string;
};

export type StructuredJobFilters = {
  jobMode: string[] | null;
  jobType: string[] | null;
  location: string | null;
  createdAfter: string | null;
  createdBefore: string | null;
};

export type ParsedJobQuery = {
  filters: StructuredJobFilters;
  keywords: string[];
};

const EMPTY_FILTERS: StructuredJobFilters = {
  jobMode: null,
  jobType: null,
  location: null,
  createdAfter: null,
  createdBefore: null,
};

function normalizeToken(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeStringList(values: unknown): string[] | null {
  if (!Array.isArray(values)) return null;
  const normalized = values.map(normalizeToken).filter(Boolean);
  return normalized.length ? normalized : null;
}

function normalizeDate(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : text;
}

export function emptyParsedJobQuery(): ParsedJobQuery {
  return {
    filters: { ...EMPTY_FILTERS },
    keywords: [],
  };
}

export function prepareJobsForLLM(allJobs: any[]): PreparedJobForLLM[] {
  return allJobs
    .filter((job) => job.Status !== 'Closed')
    .map((job) => ({
      id: String(job.Id),
      title: job.Title ?? '',
      location: {
        city: job.LocationCity,
        country: job.LocationCountry,
        countryCode: job.LocationCountryCode,
      },
      jobMode: job.JobMode,
      jobType: job.JobType,
      skills: Array.isArray(job.Skills) ? job.Skills : [],
      createdAt: fDate(job.DateCreated),
      updatedAt: fDate(job.DateLastModified),
      description: job.ContentText,
    }));
}

export function normalizeStructuredFilters(input: unknown): StructuredJobFilters {
  const raw = (input ?? {}) as Partial<StructuredJobFilters>;

  return {
    jobMode: normalizeStringList(raw.jobMode),
    jobType: normalizeStringList(raw.jobType),
    location: normalizeToken(raw.location) || null,
    createdAfter: normalizeDate(raw.createdAfter),
    createdBefore: normalizeDate(raw.createdBefore),
  };
}

export function normalizeKeywords(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.map(normalizeToken).filter(Boolean);
}

export function applyFilters(
  jobs: PreparedJobForLLM[],
  filters: StructuredJobFilters
): PreparedJobForLLM[] {
  return jobs.filter((job) => {
    const jobMode = normalizeToken(job.jobMode);
    const jobType = normalizeToken(job.jobType);

    if (filters.jobMode?.length && !filters.jobMode.includes(jobMode)) {
      return false;
    }

    if (filters.jobType?.length && !filters.jobType.includes(jobType)) {
      return false;
    }

    if (filters.location) {
      const location = `${job.location.city ?? ''} ${job.location.country ?? ''}`
        .trim()
        .toLowerCase();
      if (!location.includes(filters.location)) {
        return false;
      }
    }

    if (filters.createdAfter && job.createdAt) {
      const jobDate = new Date(job.createdAt);
      const minDate = new Date(filters.createdAfter);
      if (!Number.isNaN(jobDate.getTime()) && jobDate < minDate) {
        return false;
      }
    }

    if (filters.createdBefore && job.createdAt) {
      const jobDate = new Date(job.createdAt);
      const maxDate = new Date(filters.createdBefore);
      if (!Number.isNaN(jobDate.getTime()) && jobDate > maxDate) {
        return false;
      }
    }

    return true;
  });
}

export function rankJobs(
  jobs: PreparedJobForLLM[],
  keywords: string[]
): PreparedJobForLLM[] {
  if (!keywords.length) return jobs;

  const query = keywords.join(' ').toLowerCase();

  // 🔥 Step 1: Add manual scoring boost
  const boosted = jobs.map((job) => {
    let boost = 0;

    const title = job.title.toLowerCase();

    // Exact match → huge boost
    if (title.includes(query)) boost += 50;

    // Partial word matches
    keywords.forEach((word) => {
      if (title.includes(word.toLowerCase())) {
        boost += 10;
      }
    });

    return { ...job, __boost: boost };
  });

  // 🔥 Step 2: Fuse ranking
  const fuse = new Fuse(boosted, {
    keys: [
      { name: 'title', weight: 0.7 },
      { name: 'skills', weight: 0.3 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  });

  const results = fuse.search(query).map((r) => r.item);

  // 🔥 Step 3: Combine Fuse + boost
  return results.sort((a, b) => (b.__boost || 0) - (a.__boost || 0));
}

export type JobsFuzzyMatch = {
  job: PreparedJobForLLM;
  score: number;
};

export function fuzzySearchJobs(jobs: PreparedJobForLLM[], query: string): JobsFuzzyMatch[] {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return [];
  }

  const fuse = new Fuse(jobs, {
    keys: [
      { name: 'title', weight: 0.34 },
      { name: 'skills', weight: 0.24 },
      { name: 'description', weight: 0.18 },
      { name: 'location.city', weight: 0.1 },
      { name: 'location.country', weight: 0.07 },
      { name: 'jobMode', weight: 0.04 },
      { name: 'jobType', weight: 0.03 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
    includeScore: true,
  });

  return fuse.search(normalizedQuery).map((result) => ({
    job: result.item,
    score: result.score ?? 1,
  }));
}

export async function runJobsAndCandidatesSearch(
  query: string,
  providers: SearchProvider[],
  searchMethod: 'ai' | 'fuse' = 'fuse',
  shouldCancel?: () => boolean,
): Promise<{ jobs: ProviderSearchResult[]; candidates: ProviderSearchResult[] }> {
  if (shouldCancel?.()) {
    return { jobs: [], candidates: [] };
  }

  const jobsProvider = providers.find((p) => p.name === 'jobs-search-provider');
  const candidatesProvider = providers.find((p) => p.name === 'candidate-search-provider');

  const jobs = jobsProvider
    ? await (searchMethod === 'ai' ? jobsProvider.aiSearch : jobsProvider.fuseSearch)({ query, intent: 'jobs', shouldCancel })
    : [];
  if (shouldCancel?.()) {
    return { jobs: [], candidates: [] };
  }

  const candidates = candidatesProvider
    ? await candidatesProvider.aiSearch({ query, intent: 'candidates', shouldCancel })
    : [];
  if (shouldCancel?.()) {
    return { jobs: [], candidates: [] };
  }

  return { jobs, candidates };
}